import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocalPhoneOutlined from '@mui/icons-material/LocalPhoneOutlined';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from '@remix-run/react';
import Debug from 'debug';
import * as React from 'react';
import { useLocale } from 'remix-i18next';
import { getLanguage, languages } from '~/utils/i18n';
import Logo from './Logo';
import { MuiRemixLink } from './shared/links';

const debug = Debug('sc:mango:component:footer');

type FooterItem = {
  text: string;
  link: string;
};

type FooterColumnProps = {
  title: string;
  items: FooterItem[];
};

const FooterColumn = ({ title, items }: FooterColumnProps) => {
  return (
    <Grid item xs={12} sm={6} lg={3} sx={{ mt: { xs: 4, lg: 0 }, pr: 1.5 }}>
      <Typography variant="h6">{title}</Typography>
      <ul className="list-none p-0">
        {items.map(({ text, link }) => {
          return (
            <li key={text} style={{ marginBottom: '16px' }}>
              <Link href={link} underline="hover" color="inherit">
                {text}
              </Link>
            </li>
          );
        })}
      </ul>
    </Grid>
  );
};

const iconStyles = {
  bgcolor: 'white',
  color: 'primary.main',
  '&:hover': { color: 'primary.dark', bgcolor: 'white' },
  border: 0,
  p: 1,
};

const socialIcons = [
  { id: 1, Icon: FacebookIcon, link: undefined },
  { id: 2, Icon: TwitterIcon, link: undefined },
  { id: 3, Icon: InstagramIcon, link: undefined },
  { id: 4, Icon: LinkedInIcon, link: 'https://www.linkedin.com/company/swagcharm/' },
  { id: 5, Icon: YouTubeIcon, link: undefined },
];

const AboutColumn = () => {
  return (
    <Grid item xs={12} sm={6} lg={3} sx={{ mt: { xs: 4, lg: 0 } }}>
      <Logo isResponsive={false} />
      <Typography mt={2} color="inherit" pr={3}>
        We sell custom products for all your needs. Preset Packs for all occasions that you will enjoy.
      </Typography>
      <Grid container mt={3} alignItems="center">
        <LocalPhoneOutlined sx={{ fontSize: 16 }} />
        <Link href="tel:+1-302-208-7150" underline="none" color="inherit">
          <Typography variant="body2" ml={1.5}>
            +1-302-208-7150
          </Typography>
        </Link>
      </Grid>
      <Stack direction="row" spacing={1} mt={5}>
        {socialIcons.map(({ id, Icon, link }) => (
          <IconButton component={link ? Link : 'span'} href={link} key={id} sx={iconStyles}>
            <Icon sx={{ fontSize: 20 }} />
          </IconButton>
        ))}
      </Stack>
    </Grid>
  );
};

const ourCompany: FooterColumnProps = {
  title: 'Our company',
  items: [
    { text: 'FAQ', link: 'https://support.swagcharm.com/' },
    { text: 'Partnerships', link: 'mailto:support@swagcharm.com' },
  ],
};
const howCanWeHelp: FooterColumnProps = {
  title: 'How can we help',
  items: [
    { text: 'Track your Order', link: '/dashboard/orders' },
    { text: 'Help center', link: 'https://support.swagcharm.com/' },
  ],
};
const information: FooterColumnProps = {
  title: 'Information',
  items: [
    { text: 'Contact us', link: 'mailto:support@swagcharm.com' },
    { text: 'Privacy', link: '/privacy' },
    { text: 'Terms of use', link: '/terms' },
  ],
};

const year = new Date().getFullYear();

function Language() {
  const locale = useLocale();
  const [language, setLanguage] = React.useState(getLanguage(locale));
  const [searchParams] = useSearchParams();

  const handleChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value);
  };

  const linkSearchParams = new URLSearchParams(searchParams);

  debug('language:', language);

  return (
    <FormControl size="small">
      <Select value={language} variant="standard" onChange={handleChange} disableUnderline>
        {Object.entries(languages).map(([languageLocale, languageNativeName]) => {
          linkSearchParams.set('lng', languageLocale);
          linkSearchParams.sort();

          return (
            <MenuItem key={languageLocale} value={languageNativeName}>
              <MuiRemixLink href={'?' + linkSearchParams} underline="none" variant="body2" color="inherit">
                {languageNativeName}
              </MuiRemixLink>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default function Footer() {
  return (
    <Box sx={{ bgcolor: 'rgba(18, 18, 61, 0.87)', color: '#fff' }}>
      <Container sx={{ py: 10 }}>
        <Box>
          <Grid container spacing={0}>
            <AboutColumn />
            <FooterColumn {...ourCompany} />
            <FooterColumn {...howCanWeHelp} />
            <FooterColumn {...information} />
          </Grid>
          <hr className="bg-gray-100" style={{ marginTop: '56px', marginBottom: '56px' }} />
          <Stack
            flexDirection={{ xs: 'column-reverse', md: 'row' }}
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Typography variant="body2" component="span" mb={1}>
              {`© ${year} SwagCharm. All rights reserved.`}
            </Typography>
            {/* <Typography variant="body2" component="span">
            Region:
          </Typography> */}
            {/* <Language /> */}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
